export const FALLBACK_ILS_PER_EUR = 3.27;
export const FALLBACK_USD_PER_EUR = 1.08;

/** Fallback when the live rate API is unavailable. Override via env. */
export function defaultIlsPerEur(): number {
  const fromEnv =
    Number(process.env.REACT_APP_ILS_PER_EUR) || Number(process.env.ILS_PER_EUR);
  return fromEnv > 0 ? fromEnv : FALLBACK_ILS_PER_EUR;
}

export function defaultUsdPerEur(): number {
  const fromEnv =
    Number(process.env.REACT_APP_USD_PER_EUR) || Number(process.env.USD_PER_EUR);
  return fromEnv > 0 ? fromEnv : FALLBACK_USD_PER_EUR;
}

export interface EurRates {
  ilsPerEur: number;
  usdPerEur: number;
  live: boolean;
}

export function fallbackEurRates(): EurRates {
  return {
    ilsPerEur: defaultIlsPerEur(),
    usdPerEur: defaultUsdPerEur(),
    live: false,
  };
}

/** @deprecated Use fetchEurRates — kept for sync script compatibility */
export interface EurToIlsRateResult {
  rate: number;
  live: boolean;
}

/**
 * Live EUR → ILS and EUR → USD from one API call.
 * Uses ExchangeRate-API open endpoint (free, no API key).
 */
export async function fetchEurRates(): Promise<EurRates> {
  const fallbackIls = defaultIlsPerEur();
  const fallbackUsd = defaultUsdPerEur();
  try {
    const res = await fetch('https://open.er-api.com/v6/latest/EUR');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { rates?: { ILS?: number; USD?: number } };
    const ils = data.rates?.ILS;
    const usd = data.rates?.USD;
    if (typeof ils !== 'number' || !Number.isFinite(ils) || ils <= 0) {
      throw new Error('Invalid ILS rate in response');
    }
    if (typeof usd !== 'number' || !Number.isFinite(usd) || usd <= 0) {
      throw new Error('Invalid USD rate in response');
    }
    return { ilsPerEur: ils, usdPerEur: usd, live: true };
  } catch (err) {
    console.warn('Could not fetch live EUR exchange rates, using fallback:', err);
    return { ilsPerEur: fallbackIls, usdPerEur: fallbackUsd, live: false };
  }
}

export async function fetchEurToIlsRate(): Promise<EurToIlsRateResult> {
  const rates = await fetchEurRates();
  return { rate: rates.ilsPerEur, live: rates.live };
}
